import { useState, useEffect, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { apiPost, apiGet } from "../api";
import { Icons } from "./icons";

interface Props {
  orgId: string;
  onClose: () => void;
  onAdded: () => void;
}

type Mode = "existing" | "new";

interface UserMatch {
  id: string;
  email: string;
  display_name: string | null;
}

export function AddMemberModal({ orgId, onClose, onAdded }: Props) {
  const [mode, setMode] = useState<Mode>("new");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "operator" | "viewer">("viewer");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Existing-user search state
  const [searchQuery, setSearchQuery] = useState("");
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    if (mode !== "existing" || !searchQuery || selectedUser) {
      setMatches([]);
      return;
    }
    const timer = setTimeout(() => {
      apiGet<{ users: UserMatch[] }>(
        `/v1/users?email_search=${encodeURIComponent(searchQuery)}`,
      )
        .then((r) => setMatches(r.users.slice(0, 10)))
        .catch(() => setMatches([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, mode, selectedUser]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "new") {
        // Create user with org_id + role via admin endpoint
        await apiPost("/v1/users", {
          email: email.trim(),
          password: password.trim(),
          display_name: name.trim() || null,
          org_id: orgId,
          roles: [role],
        });
      } else {
        if (!selectedUser) { setError("Seleccioná un usuario primero."); setSubmitting(false); return; }
        await apiPost(`/v1/organizations/${orgId}/members`, {
          user_id: selectedUser.id,
          role,
        });
      }
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div onClick={onClose} className="modal-backdrop">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="modal-card"
        style={{ width: 520, display: "grid", gap: 14 }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>Agregar miembro</h2>
            <p
              className="text-sm text-muted"
              style={{ marginTop: 4, marginBottom: 0 }}
            >
              Creá un usuario nuevo o buscá uno existente por email.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icons.Close size={14} />
          </button>
        </header>

        <div
          style={{
            display: "flex",
            gap: 4,
            padding: 2,
            background: "var(--bg-2)",
            borderRadius: 8,
          }}
        >
          {(["new", "existing"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              style={{
                flex: 1,
                padding: "6px 12px",
                background: mode === m ? "var(--bg-1)" : "transparent",
                color: mode === m ? "var(--fg-1)" : "var(--fg-3)",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {m === "new" ? "Crear usuario nuevo" : "Asignar existente"}
            </button>
          ))}
        </div>

        {mode === "new" ? (
          <>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Email</span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                Nombre (opcional)
              </span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                Contraseña inicial
              </span>
              <input
                className="input"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <span className="hint">
                Min 8 caracteres. El usuario la puede cambiar después.
              </span>
            </label>
          </>
        ) : (
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Buscar usuario</span>
            {selectedUser ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--bg-2)", border: "1px solid var(--border-1)", borderRadius: 6 }}>
                <span>{selectedUser.email}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => { setSelectedUser(null); setSearchQuery(""); }}
                  style={{ marginLeft: "auto" }}
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <input
                  className="input"
                  placeholder="Buscar por email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {matches.length > 0 && (
                  <div style={{ display: "grid", gap: 2, maxHeight: 200, overflowY: "auto", border: "1px solid var(--border-1)", borderRadius: 6 }}>
                    {matches.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => { setSelectedUser({ id: u.id, email: u.email }); setSearchQuery(u.email); }}
                        style={{ textAlign: "left", padding: "6px 10px", background: "transparent", border: "none", color: "var(--fg-1)", cursor: "pointer", fontSize: 13 }}
                      >
                        <strong>{u.email}</strong>
                        {u.display_name && <span style={{ marginLeft: 6, color: "var(--fg-3)" }}>· {u.display_name}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </label>
        )}

        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Rol</span>
          <select
            className="input"
            value={role}
            onChange={(e) =>
              setRole(e.target.value as "admin" | "operator" | "viewer")
            }
          >
            <option value="viewer">viewer (solo lectura)</option>
            <option value="operator">
              operator (crea/edita services e incidents)
            </option>
            <option value="admin">admin (todo + gestión de miembros)</option>
          </select>
        </label>

        {error && (
          <p style={{ color: "var(--st-major)", fontSize: 13, margin: 0 }}>
            {error}
          </p>
        )}

        <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Agregando…" : "Agregar"}
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
}
